import { useLazyQuery } from '@apollo/client';

import { useMemo, useRef, useCallback } from 'react';

import GetPaginatedBillingCyclesQuery from '@/graphql/contracts/getPaginatedFilteredBillingCycles.query.gql';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import { BillingCycleFilterInput } from '@/graphql/contracts/_generated_/contracts';
import {
  GetPaginatedFilteredBillingCycles,
  GetPaginatedFilteredBillingCyclesVariables,
}   from '@/graphql/contracts/_generated_/getPaginatedFilteredBillingCycles.query';
import { notificationService } from '@/services/notification/notification.client';
import { PaginatedFilteredGridData } from '@/utils/data-helpers/PaginatedFilteredGridData';
import { ALL_ITEMS } from '@/config/common.config';

export type PaginatedFilteredBillingCycles = PaginatedFilteredGridData<BillingCycleFilterInput>;

export default function useGetBillingCycles() {
  // Store last params for refetch
  const lastParamsRef = useRef<PaginatedFilteredBillingCycles | null>(null);

  const [_getBillingCycles, { loading, error, data }] = 
    useLazyQuery<GetPaginatedFilteredBillingCycles, GetPaginatedFilteredBillingCyclesVariables>(
      GetPaginatedBillingCyclesQuery, {
        client: apolloClientContracts,
        fetchPolicy: 'network-only',
        onError: (err) =>
          notificationService.open({
            title: 'Error',
            text: err.message,
            type: 'danger',
          }),
      });

  const getBillingCycles = useCallback((params: PaginatedFilteredBillingCycles) => {
    lastParamsRef.current = params;
    _getBillingCycles({
      variables: {
        first: ALL_ITEMS,
        ...params,
      } as GetPaginatedFilteredBillingCyclesVariables,
    });
  }, [_getBillingCycles]);

  // Refetch with last used params
  const refetchBillingCycles = useCallback(() => {
    if (lastParamsRef.current) {
      _getBillingCycles({
        variables: {
          first: ALL_ITEMS,
          ...lastParamsRef.current,
        } as GetPaginatedFilteredBillingCyclesVariables,
      });
    }
  }, [_getBillingCycles]);

  return useMemo(() => ({
    getBillingCycles,
    refetchBillingCycles,
    isLoadingBillingCycles: loading,
    errorBillingCycles: error,
    billingCycles: data?.paginatedFilteredBillingCycles?.items ?? []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [loading, error, data?.paginatedFilteredBillingCycles?.items, getBillingCycles, refetchBillingCycles]);
}
